import { useCallback, useMemo } from 'react';

import RecordPaymentModal from 'modules/Tax/QuarterlyEstimatesPageV2/components/RecordPaymentModal';
import { QTEType } from 'modules/Tax/types';
import { SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';

interface RecordTaxPaymentTaskProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
    taxType: QTEType;
}

/**
 * Task context values are template-declared, so a period can arrive as a number or as a numeric
 * string ("3"). Anything that is not a whole number is treated as absent rather than coerced.
 *
 * Safe-integer rather than merely finite, because the modal's own guards do not catch a fractional
 * year: `Yup.number().min(1900)` accepts 2026.5 and it reaches the payload as `year: 2026.5`. A
 * fractional quarter is caught downstream by `oneOf([1,2,3,4])`, but rejecting both here keeps the
 * rule in one place instead of relying on which field the schema happens to pin harder.
 */
function readNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
        return Number.isSafeInteger(value) ? value : undefined;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isSafeInteger(parsed) ? parsed : undefined;
    }
    return undefined;
}

/**
 * The {year, quarter} both task and group context carry. Missing or unparseable values are dropped
 * so the member fills them in — the modal stays usable either way, which a dead CTA would not be.
 */
function readPeriod(context: unknown): { year?: number; quarter?: number } {
    if (typeof context !== 'object' || context === null) {
        return {};
    }
    const { year, quarter } = context as Record<string, unknown>;
    const parsedQuarter = readNumber(quarter);

    return {
        year: readNumber(year),
        quarter: parsedQuarter !== undefined && parsedQuarter >= 1 && parsedQuarter <= 4 ? parsedQuarter : undefined,
    };
}

/**
 * Adapts the custom-submit-action props ({open, onClose, task}) onto RecordPaymentModal's
 * ({isOpen, onClose, onSuccess, prefilledValues}). Shared by the two registered record-payment
 * tasks, which differ only in the tax type they pin.
 *
 * `taxType` is the whole reason there are two wrappers: it never reaches the payload, but the modal
 * derives `payment_type` from it (BUSINESS -> business, else quarterly), and that is the field both
 * the Fees and Franchise history filter and the backend's card completion key off.
 */
export default function RecordTaxPaymentTask({ open, onClose, task, taxType }: Readonly<RecordTaxPaymentTaskProps>) {
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();

    /*
     * Identity matters, not just contents: RecordPaymentModal memoizes its Formik initialValues on
     * this object and runs with enableReinitialize, so a literal rebuilt each render would reset
     * the form under the member as they type.
     */
    const prefilledValues = useMemo(
        () => ({ tax_type: taxType, ...readPeriod(task.context) }),
        [taxType, task.context]
    );

    /*
     * Refetch only — no status write. The backend completes both children of the card when the
     * payment posts, so setting a status here would race it. Passing `task` keeps the grouped branch
     * of the shared helper: only the task list is invalidated, leaving the group card and priority
     * feed to refresh when the drawer closes.
     */
    const handleSuccess = useCallback(() => {
        invalidateMemberTaskQueries(task);
        onClose();
    }, [invalidateMemberTaskQueries, task, onClose]);

    return (
        <RecordPaymentModal
            isOpen={open}
            onClose={onClose}
            onSuccess={handleSuccess}
            prefilledValues={prefilledValues}
        />
    );
}
